import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Award, Gift, ShoppingBag, Sparkles, Coins, History, ArrowDownRight, Crown, Percent } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { pickTier, nextTier, tierStyle } from "@/lib/loyalty";
import { TierBadge } from "@/components/TierBadge";

export default function Rewards() {
  const { t, lang } = useLanguage();
  const { user } = useAuth();
  const [cfg, setCfg] = useState({ point_value_aed: 0.2, min_redeem: 5, tiers: [] });
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    api.get("/loyalty-config").then(({ data }) => setCfg(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user && user.role !== "admin") {
      api.get("/orders/mine").then(({ data }) => setOrders(data)).catch(() => {});
    }
  }, [user]);

  const history = orders
    .filter((o) => (o.points_earned || 0) > 0 || (o.points_redeemed || 0) > 0)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const balance = user?.points || 0;
  const lifetime = user?.points_lifetime ?? balance;
  const balanceWorth = (balance * (cfg.point_value_aed || 0)).toFixed(2);
  const tiers = cfg.tiers || [];
  const current = pickTier(tiers, lifetime);
  const next = nextTier(tiers, lifetime);
  const progress = next
    ? Math.min(100, Math.round(((lifetime - (current?.min_points || 0)) / (next.min_points - (current?.min_points || 0))) * 100))
    : 100;

  const steps = [
    {
      icon: Coins,
      title: t("howToEarn"),
      desc: t("howToEarnDesc"),
    },
    {
      icon: Gift,
      title: t("howToRedeem"),
      desc: t("howToRedeemDesc"),
    },
    {
      icon: Sparkles,
      title: t("conversionRate"),
      desc:
        t("onePointEquals").replace("{v}", cfg.point_value_aed) +
        " · " +
        t("minToRedeem").replace("{m}", cfg.min_redeem),
    },
  ];

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-14 page-enter" data-testid="rewards-page">
      {/* Hero */}
      <div className="text-center mb-14">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl border border-[#D4AF37]/40 bg-[#D4AF37]/5 text-[#D4AF37] mb-6">
          <Award size={28} />
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-4 gold-gradient-text">
          {t("rewardsTitle")}
        </h1>
        <p className="text-[#9CA3AF] max-w-xl mx-auto">{t("rewardsSubtitle")}</p>
      </div>

      {/* Balance card */}
      <div
        className="relative overflow-hidden rounded-3xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#121214] to-[#0A0A0B] p-8 mb-14"
        data-testid="rewards-balance-card"
      >
        <div className="absolute -top-16 ltr:-right-16 rtl:-left-16 w-48 h-48 rounded-full bg-[#D4AF37]/10 blur-3xl" />
        {user ? (
          <div className="relative">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-xs uppercase tracking-widest text-[#9CA3AF]">
                    {t("yourBalance")}
                  </span>
                  {current && <TierBadge tier={current} />}
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-6xl text-[#D4AF37] num-ltr" data-testid="rewards-balance">
                    {balance}
                  </span>
                  <span className="text-sm text-[#9CA3AF]">{t("points")}</span>
                </div>
                <div className="text-sm text-[#F3F4F6]/70 mt-2 num-ltr">
                  {t("worth")} {balanceWorth} {t("currency")} · {t("lifetimePoints")}: {lifetime}
                </div>
              </div>
              <Link
                to="/shop"
                data-testid="rewards-shop-btn"
                className="btn-gold px-7 py-3 rounded-full inline-flex items-center gap-2 flex-shrink-0"
              >
                <ShoppingBag size={18} /> {t("startEarning")}
              </Link>
            </div>

            {/* Progress to next tier */}
            <div className="mt-6" data-testid="tier-progress">
              {next ? (
                <>
                  <div className="flex items-center justify-between text-xs mb-2">
                    <span className="text-[#9CA3AF]">
                      {t("pointsToNext")
                        .replace("{n}", Math.max(0, next.min_points - lifetime))
                        .replace("{tier}", lang === "ar" ? next.name_ar : next.name_en)}
                    </span>
                    <span style={{ color: tierStyle(next.key).color }} className="inline-flex items-center gap-1">
                      <Crown size={12} /> {lang === "ar" ? next.name_ar : next.name_en}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-[#27272A] overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${progress}%`, backgroundColor: tierStyle(current?.key).color }}
                    />
                  </div>
                </>
              ) : (
                <div className="text-xs text-[#D4AF37] inline-flex items-center gap-1">
                  <Crown size={13} /> {t("maxTier")}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div className="text-[#F3F4F6]/80">{t("loginToSeePoints")}</div>
            <Link
              to="/login"
              data-testid="rewards-login-btn"
              className="btn-gold px-7 py-3 rounded-full inline-flex items-center gap-2 flex-shrink-0"
            >
              {t("login")}
            </Link>
          </div>
        )}
      </div>

      {/* How it works */}
      <div className="grid md:grid-cols-3 gap-5">
        {steps.map((s, i) => (
          <div
            key={i}
            className="bg-[#121214] border border-[#27272A] rounded-2xl p-6 hover-lift"
            data-testid={`rewards-step-${i}`}
          >
            <div className="w-11 h-11 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] mb-4">
              <s.icon size={20} />
            </div>
            <h3 className="font-display text-lg mb-2">{s.title}</h3>
            <p className="text-sm text-[#9CA3AF] leading-relaxed">{s.desc}</p>
          </div>
        ))}
      </div>

      {/* Tier benefits */}
      {tiers.length > 0 && (
        <div className="mt-14" data-testid="tier-benefits">
          <div className="flex items-center gap-2 mb-5">
            <Crown size={18} className="text-[#D4AF37]" />
            <h2 className="font-display text-xl">{t("tierBenefits")}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {tiers.map((tr) => {
              const st = tierStyle(tr.key);
              const isCurrent = current && current.key === tr.key;
              return (
                <div
                  key={tr.key}
                  data-testid={`tier-card-${tr.key}`}
                  className="rounded-2xl border p-5 bg-[#121214] relative"
                  style={{ borderColor: isCurrent ? st.border : "#27272A" }}
                >
                  {isCurrent && (
                    <span className="absolute top-3 ltr:right-3 rtl:left-3 text-[9px] uppercase tracking-widest" style={{ color: st.color }}>
                      {t("currentTier")}
                    </span>
                  )}
                  <TierBadge tier={tr} />
                  <div className="mt-4 space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-[#9CA3AF] inline-flex items-center gap-1"><Sparkles size={13} /> {t("tierMultiplier")}</span>
                      <span className="num-ltr" style={{ color: st.color }}>×{tr.multiplier}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#9CA3AF] inline-flex items-center gap-1"><Percent size={13} /> {t("tierExclusiveDiscount")}</span>
                      <span className="num-ltr" style={{ color: st.color }}>{tr.discount_percent}%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#9CA3AF] inline-flex items-center gap-1"><Award size={13} /> {t("tierMinLabel")}</span>
                      <span className="num-ltr text-[#F3F4F6]/80">{tr.min_points}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Points history */}
      {user && (
        <div className="mt-14" data-testid="points-history">
          <div className="flex items-center gap-2 mb-5">
            <History size={18} className="text-[#D4AF37]" />
            <h2 className="font-display text-xl">{t("pointsHistory")}</h2>
          </div>

          {history.length === 0 ? (
            <div className="text-sm text-[#9CA3AF] bg-[#121214] border border-[#27272A] rounded-2xl p-6 text-center" data-testid="no-points-history">
              {t("noPointsHistory")}
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((o) => (
                <div
                  key={o.id}
                  className="bg-[#121214] border border-[#27272A] rounded-xl p-4 flex items-center justify-between gap-4 flex-wrap"
                  data-testid={`history-row-${o.id}`}
                >
                  <div>
                    <div className="text-sm">
                      {t("orderLabel")} #<span className="num-ltr">{o.id.slice(0, 8)}</span>
                    </div>
                    <div className="text-xs text-[#9CA3AF] num-ltr">
                      {new Date(o.created_at).toLocaleDateString(lang === "ar" ? "ar-EG" : "en-GB")}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {(o.points_earned || 0) > 0 && (
                      <span className="inline-flex items-center gap-1 text-xs text-[#D4AF37] num-ltr px-3 py-1 rounded-full border border-[#D4AF37]/30">
                        <Award size={12} /> {t("orderPointsEarned")}: +{o.points_earned}
                      </span>
                    )}
                    {(o.points_redeemed || 0) > 0 && (
                      <span className="inline-flex items-center gap-1 text-xs text-[#10B981] num-ltr px-3 py-1 rounded-full border border-[#10B981]/30">
                        <ArrowDownRight size={12} /> {t("pointsRedeemedLabel")}: −{o.points_redeemed} ({(o.points_discount || 0).toFixed(2)} {t("currency")})
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
